import { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';

const menuGroups = [
  {
    labelKey: 'dashboard.menu.group.main',
    items: [
      { labelKey: 'auth:dashboard', fallback: 'Dashboard', href: '/my', icon: 'ri-dashboard-line' },
      { labelKey: 'auth:myQuotes', fallback: 'My Quotes', href: '/my/quotes', icon: 'ri-file-list-3-line' },
      { labelKey: 'auth:myOrders', fallback: 'My Orders', href: '/my/orders', icon: 'ri-truck-line' },
      { labelKey: 'dashboard.payments.title', fallback: 'Payments', href: '/my/payments', icon: 'ri-bank-card-line' },
      { labelKey: 'dashboard.shipping.title', fallback: 'Shipping', href: '/my/shipping', icon: 'ri-ship-line' },
    ],
  },
  {
    labelKey: 'dashboard.menu.group.products',
    items: [
      { labelKey: 'auth:aiPreviews', fallback: 'AI Finish Previews', href: '/my/finish-previews', icon: 'ri-magic-line' },
      { labelKey: 'auth:savedProducts', fallback: 'Saved Products', href: '/my/favorites', icon: 'ri-heart-line' },
      { labelKey: 'dashboard.samples.title', fallback: 'Samples', href: '/my/samples', icon: 'ri-flask-line' },
      { labelKey: 'dashboard.files.title', fallback: 'Files', href: '/my/files', icon: 'ri-folder-upload-line' },
    ],
  },
  {
    labelKey: 'dashboard.menu.group.account',
    items: [
      { labelKey: 'auth:companyProfile', fallback: 'Company Profile', href: '/my/company', icon: 'ri-building-4-line' },
      { labelKey: 'auth:shippingAddress', fallback: 'Shipping Address', href: '/my/addresses', icon: 'ri-map-pin-line' },
      { labelKey: 'auth:accountSettings', fallback: 'Account Settings', href: '/my/settings', icon: 'ri-settings-3-line' },
      { labelKey: 'dashboard.messages.title', fallback: 'Messages', href: '/my/messages', icon: 'ri-message-3-line' },
      { labelKey: 'dashboard.market.title', fallback: 'Market', href: '/my/market', icon: 'ri-global-line' },
    ],
  },
];

export default function DashboardLayout() {
  const { t } = useTranslation(['dashboard', 'auth']);
  const location = useLocation();
  const { user, profile, company, isAdmin, isSuperAdmin, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const displayName = company?.company_name || profile?.full_name || user?.email?.split('@')[0] || '';
  const email = user?.email || '';
  const avatarInitial = (displayName || email || 'U').charAt(0).toUpperCase();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node) && mobileOpen) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileOpen]);

  const isActive = (href: string) => {
    if (href === '/my') return location.pathname === '/my';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-cream-50 flex">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-ink-100 h-14 flex items-center justify-between px-4">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-ink-700 hover:bg-cream-50 rounded-lg transition-colors"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
        </button>
        <span className="text-sm font-semibold text-ink-700">{t('dashboard.title')}</span>
        <div className="w-8" />
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-60 shrink-0 bg-white border-r border-ink-100 overflow-y-auto transition-transform duration-300 lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-4 flex flex-col min-h-full">
          {/* Logo */}
          <div className="mb-6">
            <a href="/" className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 bg-ink-700 flex items-center justify-center">
                <i className="ri-box-3-line text-white text-sm" />
              </div>
              <span className="text-base font-bold text-ink-700">GBCOSPACK</span>
            </a>
            {user && (
              <div className="px-3 py-2.5 bg-cream-50 rounded-lg border border-ink-100">
                <div className="flex items-center gap-2">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-ink-100 flex items-center justify-center text-xs font-bold text-ink-600 shrink-0">
                      {avatarInitial}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink-700 truncate">{displayName}</p>
                    <p className="text-xs text-ink-400 truncate">{email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-cream-100 text-ink-500 border border-ink-100">
                    {t('auth:customerAccount', { defaultValue: 'Customer Account' })}
                  </span>
                  {isSuperAdmin && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-ink-700 text-white">
                      {t('auth:adminBadge', { defaultValue: 'Admin' })}
                    </span>
                  )}
                  {isAdmin && !isSuperAdmin && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-ink-500 text-white">
                      {t('auth:adminBadge', { defaultValue: 'Admin' })}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="space-y-6">
            {menuGroups.map((group) => (
              <div key={group.labelKey}>
                <p className="px-3 mb-1.5 text-[10px] font-semibold text-ink-300 uppercase tracking-wider">
                  {t(group.labelKey)}
                </p>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const active = isActive(item.href);
                    const label = t(item.labelKey, { defaultValue: item.fallback });
                    return (
                      <a
                        key={item.href}
                        href={item.href}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          active
                            ? 'bg-ink-50 text-ink-700'
                            : 'text-ink-500 hover:bg-cream-50 hover:text-ink-700'
                        }`}
                      >
                        <div className={`w-5 h-5 flex items-center justify-center ${active ? 'text-warm-500' : 'text-ink-400'}`}>
                          <i className={`${item.icon} text-base`} />
                        </div>
                        <span>{label}</span>
                        {active && (
                          <div className="ml-auto w-1 h-1 rounded-full bg-warm-500" />
                        )}
                      </a>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Admin Console */}
          {(isAdmin || isSuperAdmin) && (
            <div className="mt-6">
              <a
                href="/admin"
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center text-ink-400">
                  <i className="ri-shield-user-line text-base" />
                </div>
                <span>{t('auth:adminConsole', { defaultValue: 'Admin Console' })}</span>
              </a>
            </div>
          )}

          <div className="mt-auto">
            {/* Back to home */}
            <div className="pt-4 border-t border-ink-100">
              <a
                href="/"
                className="flex items-center gap-2 px-3 py-2 text-xs text-ink-400 hover:text-ink-600 transition-colors"
              >
                <i className="ri-arrow-left-line" />
                {t('dashboard.menu.backToHome')}
              </a>
            </div>

            {/* Logout */}
            {user && (
              <div className="mt-1 pb-1">
                <button
                  onClick={() => {
                    signOut().catch(() => {});
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-logout-box-r-line text-base" />
                  </div>
                  <span>{t('auth:logout', { defaultValue: 'Logout' })}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}